import React, { useEffect, useState } from 'react';
import { gsap } from 'gsap';
import Modal from './Modal';
import './Slide2.css';

const Slide2Component = () => {
  const [typedText, setTypedText] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);

  const openModal = (cardType) => {
    setModalContent(getModalContent(cardType));
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const getModalContent = (type) => {
    const contents = {
      semantic: {
        title: 'Recherche Sémantique',
        icon: 'bi bi-search',
        content: (
          <>
            <div className="modal-section">
              <h3><i className="bi bi-lightbulb"></i> Comprendre l'intention</h3>
              <p>
                La recherche sémantique révolutionne la manière dont nous interagissons avec l'information en ligne.
                Contrairement aux moteurs de recherche traditionnels qui se concentrent sur la correspondance exacte des mots-clés,
                MarIAne analyse le contexte et l'intention derrière votre requête pour vous offrir des résultats vraiment pertinents.
              </p>
              <p>
                Imaginez que vous recherchiez "comment le café affecte le sommeil". Un moteur classique vous donnerait des pages
                contenant ces mots exacts, mais MarIAne comprend que vous vous interrogez sur les effets de la caféine sur
                votre cycle circadien et vous propose des articles scientifiques, des études médicales et des conseils pratiques
                adaptés à votre question réelle.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80"
              alt="Intelligence artificielle et recherche"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-diagram-3"></i> Analyse contextuelle avancée</h3>
              <p>
                Notre moteur utilise des algorithmes sophistiqués de traitement du langage naturel (NLP) pour décoder
                les nuances linguistiques, les synonymes, et même les expressions idiomatiques. Cette approche permet
                de comprendre non seulement ce que vous écrivez, mais aussi ce que vous voulez vraiment trouver.
              </p>
              <p>
                Le système prend en compte l'historique de votre session de recherche pour affiner progressivement
                sa compréhension de vos besoins, tout en respectant votre vie privée grâce au traitement local des données.
              </p>
            </div>

            <div className="modal-highlight">
              <i className="bi bi-quote"></i> "La recherche sémantique transforme les mots en intentions,
              et les intentions en réponses précises. C'est l'avenir de la navigation web intelligente."
            </div>

            <div className="modal-section">
              <h3><i className="bi bi-star"></i> Bénéfices concrets</h3>
              <p>
                Gagnez un temps précieux en trouvant exactement ce que vous cherchez dès la première requête.
                Découvrez des informations connexes pertinentes auxquelles vous n'auriez pas pensé.
                Bénéficiez d'une expérience de recherche qui s'améliore à mesure que vous l'utilisez,
                sans jamais compromettre votre vie privée.
              </p>
            </div>
          </>
        )
      },
      assistant: {
        title: 'Assistant IA Temps Réel',
        icon: 'bi bi-robot',
        content: (
          <>
            <div className="modal-section">
              <h3><i className="bi bi-chat-dots"></i> Votre compagnon de recherche</h3>
              <p>
                L'assistant IA de MarIAne n'est pas qu'un simple chatbot. C'est un véritable partenaire de recherche
                qui vous accompagne tout au long de votre navigation. Il comprend vos questions en langage naturel,
                affine vos recherches et vous guide vers les informations les plus pertinentes avec une approche
                conversationnelle naturelle et intuitive.
              </p>
              <p>
                Que vous cherchiez à approfondir un sujet complexe, à comparer différentes sources ou simplement
                à clarifier une information, l'assistant est là pour vous aider en temps réel, sans délai,
                sans publicité, sans compromis.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80"
              alt="Assistant IA conversationnel"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-arrow-repeat"></i> Dialogue interactif et contextuel</h3>
              <p>
                L'assistant maintient le contexte de votre conversation, vous permettant de poser des questions
                de suivi sans avoir à répéter les informations précédentes. Il peut reformuler, synthétiser ou
                approfondir les réponses selon vos besoins spécifiques.
              </p>
              <p>
                Grâce à son intégration profonde avec le moteur de recherche, l'assistant peut citer ses sources,
                vous proposer d'explorer des angles différents et même anticiper vos besoins d'information
                avant que vous ne les formuliez explicitement.
              </p>
            </div>

            <div className="modal-highlight">
              <i className="bi bi-quote"></i> "Un assistant IA qui comprend vraiment vos besoins,
              sans jamais utiliser vos données pour vous profiler ou vous cibler. C'est la promesse de MarIAne."
            </div>

            <div className="modal-section">
              <h3><i className="bi bi-shield-check"></i> Intelligence sans surveillance</h3>
              <p>
                Contrairement aux assistants IA des géants du web, notre assistant fonctionne localement.
                Vos conversations ne sont jamais envoyées à des serveurs distants, vos données ne sont jamais
                analysées à des fins commerciales, et votre vie privée reste intacte. Intelligence oui, surveillance non.
              </p>
            </div>
          </>
        )
      },
      local: {
        title: 'IA Embarquée Locale',
        icon: 'bi bi-cpu-fill',
        content: (
          <>
            <div className="modal-section">
              <h3><i className="bi bi-hdd"></i> La puissance sur votre machine</h3>
              <p>
                MarIAne intègre des modèles de langage de pointe directement dans votre navigateur.
                Cette approche révolutionnaire signifie que l'intelligence artificielle travaille pour vous
                sans jamais avoir besoin d'envoyer vos données à des serveurs externes. Tout se passe localement,
                sur votre appareil, sous votre contrôle total.
              </p>
              <p>
                Les modèles sont optimisés pour fonctionner efficacement même sur des machines modestes,
                utilisant des techniques de compression et de quantification avancées qui préservent
                les capacités de compréhension tout en réduisant l'empreinte mémoire.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80"
              alt="Traitement local et confidentialité"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-lightning-charge"></i> Performance et réactivité</h3>
              <p>
                Le traitement local élimine la latence réseau. Vos requêtes sont analysées instantanément,
                les réponses arrivent en temps réel, et l'expérience reste fluide même sans connexion internet
                pour les fonctionnalités de base. C'est la rapidité du cloud sans ses inconvénients en matière
                de confidentialité.
              </p>
              <p>
                Les modèles sont régulièrement mis à jour pour bénéficier des dernières avancées en IA,
                tout en maintenant une compatibilité optimale avec différentes configurations matérielles.
              </p>
            </div>

            <div className="modal-highlight">
              <i className="bi bi-quote"></i> "L'IA locale, c'est la garantie que vos pensées,
              vos recherches et vos données restent privées. Aucun serveur distant, aucun profiling,
              aucune exploitation commerciale."
            </div>

            <div className="modal-section">
              <h3><i className="bi bi-globe"></i> Fonctionnement hors ligne</h3>
              <p>
                Une fois les modèles téléchargés, de nombreuses fonctionnalités de MarIAne continuent
                de fonctionner même sans connexion internet. Vous pouvez analyser des documents,
                poser des questions sur du contenu en cache, et bénéficier de l'assistance IA où que vous soyez.
              </p>
            </div>
          </>
        )
      },
      enriched: {
        title: 'Résultats Enrichis',
        icon: 'bi bi-bar-chart-line',
        content: (
          <>
            <div className="modal-section">
              <h3><i className="bi bi-eye"></i> Au-delà des liens bleus</h3>
              <p>
                Fini l'époque où les résultats de recherche se résumaient à une liste de liens bleus
                accompagnés de brèves descriptions. MarIAne transforme chaque recherche en une expérience
                d'information enrichie, où les données sont structurées, visualisées et synthétisées
                pour vous faire gagner du temps et améliorer votre compréhension.
              </p>
              <p>
                Les résultats incluent des résumés générés par IA qui capturent l'essentiel de chaque page,
                des extraits pertinents mis en évidence, et des visualisations interactives lorsque
                les données s'y prêtent. C'est la recherche web repensée pour l'ère de l'information intelligente.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80"
              alt="Visualisation de données et analyse"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-graph-up"></i> Synthèse intelligente</h3>
              <p>
                Notre IA analyse automatiquement les multiples sources trouvées pour vous présenter
                une synthèse cohérente. Elle identifie les points de consensus, les divergences d'opinion,
                et les informations complémentaires pour vous donner une vue d'ensemble complète en un coup d'œil.
              </p>
              <p>
                Les graphiques, tableaux comparatifs et chronologies sont générés automatiquement
                lorsque pertinent, transformant des données brutes en insights visuels faciles à comprendre
                et à mémoriser.
              </p>
            </div>

            <div className="modal-highlight">
              <i className="bi bi-quote"></i> "Transformer l'information en connaissance :
              c'est la promesse des résultats enrichis de MarIAne. Comprendre plus vite, mieux, plus profondément."
            </div>

            <div className="modal-section">
              <h3><i className="bi bi-list-check"></i> Citations et crédibilité</h3>
              <p>
                Chaque information synthétisée est accompagnée de ses sources, permettant de vérifier
                la provenance des données et d'approfondir si nécessaire. La transparence est au cœur
                de notre approche : vous savez toujours d'où vient l'information et vous pouvez juger
                de sa crédibilité par vous-même.
              </p>
            </div>
          </>
        )
      }
    };

    return contents[type];
  };

  useEffect(() => {
    const tl = gsap.timeline();

    // État initial : on a exactement la slide 1
    tl.set('.slide2-initial-state', { opacity: 1 });

    // 1. Typewriter dans la barre de recherche (1 seconde après)
    tl.add(() => {
      const text = "Intelligence artificielle et recherche sémantique";
      let index = 0;
      const typeInterval = setInterval(() => {
        if (index <= text.length) {
          setTypedText(text.slice(0, index));
          index++;
        } else {
          clearInterval(typeInterval);
        }
      }, 40);
    }, '+=0.8');

    // 2. Pulse du bouton search
    tl.to('.slide2-search-button', {
      scale: 1.1,
      boxShadow: '0 0 40px rgba(0, 212, 255, 0.8)',
      duration: 0.3,
      repeat: 1,
      yoyo: true
    }, '+=2');

    // 3. Les features cards de la slide 1 disparaissent
    tl.to('.slide2-features', {
      opacity: 0,
      y: 30,
      duration: 0.6,
      ease: 'power2.in'
    }, '+=0.3');

    // 4. Tout le contenu initial disparaît avec un léger zoom out
    tl.to('.slide2-initial-state', {
      opacity: 0,
      scale: 0.95,
      duration: 0.8,
      ease: 'power2.inOut'
    }, '+=0.2');

    // 5. Le header apparaît avec un léger slide depuis le haut
    tl.fromTo('.search-results-header',
      { opacity: 0, y: -20 },
      { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' }
    , '-=0.4');

    // 6. Le contenu du header devient visible
    tl.to('.header-content-slide2', {
      opacity: 1,
      duration: 0.5
    }, '-=0.3');

    tl.to(['.logo-small-slide2', '.search-form-small-slide2'], {
      opacity: 1,
      duration: 0.5
    }, '-=0.5');

    // 7. Ligne séparatrice
    tl.fromTo('.corporate-separator',
      { scaleX: 0 },
      { scaleX: 1, duration: 0.8, ease: 'power2.inOut' }
    );

    // 8. Titre principal
    tl.fromTo('.slide2-main-title',
      { opacity: 0, y: 40 },
      { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out' }
    , '-=0.4');

    // 9. Les nouvelles cards apparaissent
    tl.fromTo('.tech-card',
      { opacity: 0, y: 50, scale: 0.95 },
      { opacity: 1, y: 0, scale: 1, duration: 0.7, stagger: 0.12, ease: 'power2.out' }
    );

    return () => tl.kill();
  }, []);

  return (
    <div className="slide-content slide-2-transition">
      <div className="tech-grid-bg"></div>

      {/* État initial = copie de slide 1 */}
      <div className="slide2-initial-state">
        <div className="slide2-logo">
          <h1 className="logo-main">
            MAR<span className="neon-text">IA</span>NE
          </h1>
          <p className="logo-tagline">Managed Assistant of Research with Intelligence Artificial for Navigation Ethic</p>
        </div>

        <div className="slide2-search-container">
          <div className="slide2-search-box">
            <input
              type="text"
              className="slide2-search-input"
              value={typedText}
              readOnly
              placeholder="Rechercher sur MarIAne..."
            />
            <button className="slide2-search-button">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
            </button>
          </div>
        </div>

        <div className="slide2-features">
          <div className="slide2-feature-item">
            <div className="feature-check">✓</div>
            <span>Recherche sémantique intelligente</span>
          </div>
          <div className="slide2-feature-item">
            <div className="feature-check">✓</div>
            <span>Assistant IA intégré en temps réel</span>
          </div>
          <div className="slide2-feature-item">
            <div className="feature-check">✓</div>
            <span>Navigation enrichie par l'intelligence artificielle</span>
          </div>
          <div className="slide2-feature-item">
            <div className="feature-check">✓</div>
            <span>Éthique et respect de la vie privée avant tout</span>
          </div>
        </div>
      </div>

      {/* Header SearchResults */}
      <header className="search-results-header">
        <div className="header-content-slide2">
          <div className="logo-small-slide2">
            MAR<span className="neon-text">IA</span>NE
          </div>
          <form className="search-form-small-slide2">
            <div className="search-box-small-slide2">
              <input
                type="text"
                className="search-input-small-slide2"
                placeholder="Rechercher..."
                value={typedText}
                readOnly
              />
              <button type="button" className="search-button-small-slide2">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </button>
            </div>
          </form>
        </div>
      </header>

      <div className="corporate-separator"></div>

      <div className="slide2-main-title">
        <h1 className="corporate-h1">Le Moteur de Recherche</h1>
        <p className="corporate-sub">Technologie de recherche sémantique nouvelle génération</p>
      </div>

      <div className="tech-cards-grid">
        <div className="tech-card" onClick={() => openModal('semantic')} style={{ cursor: 'pointer' }}>
          <div className="card-badge">01</div>
          <div className="card-icon-box">
            <i className="bi bi-search"></i>
          </div>
          <h3 className="card-h3">Recherche Sémantique</h3>
          <p className="card-p">
            Analyse contextuelle avancée pour des résultats pertinents
            basés sur l'intention réelle de l'utilisateur.
          </p>
        </div>

        <div className="tech-card" onClick={() => openModal('assistant')} style={{ cursor: 'pointer' }}>
          <div className="card-badge">02</div>
          <div className="card-icon-box">
            <i className="bi bi-robot"></i>
          </div>
          <h3 className="card-h3">Assistant IA Temps Réel</h3>
          <p className="card-p">
            Agent conversationnel intégré pour affiner les recherches
            et guider l'utilisateur de manière interactive.
          </p>
        </div>

        <div className="tech-card" onClick={() => openModal('local')} style={{ cursor: 'pointer' }}>
          <div className="card-badge">03</div>
          <div className="card-icon-box">
            <i className="bi bi-cpu-fill"></i>
          </div>
          <h3 className="card-h3">IA Embarquée Locale</h3>
          <p className="card-p">
            Modèles de langage intégrés pour un traitement local
            garantissant confidentialité et performance.
          </p>
        </div>

        <div className="tech-card" onClick={() => openModal('enriched')} style={{ cursor: 'pointer' }}>
          <div className="card-badge">04</div>
          <div className="card-icon-box">
            <i className="bi bi-bar-chart-line"></i>
          </div>
          <h3 className="card-h3">Résultats Enrichis</h3>
          <p className="card-p">
            Interface enrichie avec résumés IA, visualisations
            et synthèse intelligente des informations.
          </p>
        </div>
      </div>

      {modalContent && (
        <Modal
          isOpen={modalOpen}
          onClose={closeModal}
          title={modalContent.title}
          icon={modalContent.icon}
          content={modalContent.content}
        />
      )}
    </div>
  );
};

export default Slide2Component;
